import {useState} from "react";
import type {SimulationResult} from "@signum/api-contracts";
import {Pagination, TableToolbar} from "./TableControls.js";

export function AverageInsiderInvestmentSummary({summary}: {
  summary?: SimulationResult["averageInsiderInvestmentFilter"];
}) {
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  if (!summary?.enabled) return null;
  const rows = summary.evidence.filter((row) =>
    (outcome === "ALL" || row.outcome === outcome) &&
    `${row.issuerSymbol} ${row.analysisDate}`.toLowerCase().includes(query.trim().toLowerCase()));
  const currentPage = Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize)));
  const money = (value: number) => value.toLocaleString("en-US", {style: "currency", currency: "USD", maximumFractionDigits: 4});
  return <section className="panel">
    <div className="panel-title"><div><span className="eyebrow">Filter evidence</span>
      <h2>Average investment per insider</h2>
      <p>{summary.comparison === "ABOVE" ? ">" : "<"} {money(summary.threshold)} · Total qualifying purchase amount ÷ distinct insider buyers.</p>
      <p>{summary.matchedCandidateCount.toLocaleString()} passed · {summary.excludedCandidateCount.toLocaleString()} did not pass · {summary.missingCandidateCount.toLocaleString()} missing evidence. These counts apply to this filter alone; other rules still apply.</p>
    </div></div>
    <details><summary>Review candidate calculations</summary>
      <TableToolbar query={query} onQueryChange={(value) => {setQuery(value); setPage(1);}} placeholder="Search symbol or date">
        <label>Outcome <select value={outcome} onChange={(event) => {setOutcome(event.target.value); setPage(1);}}>
          <option value="ALL">All</option><option value="PASS">Passed</option><option value="FAIL">Did not pass</option><option value="MISSING">Missing evidence</option>
        </select></label>
      </TableToolbar>
      <div className="table-scroll"><table><thead><tr><th>Date</th><th>Symbol</th><th>Total invested (USD)</th><th>Distinct buyers</th><th>Average (USD)</th><th>Outcome</th></tr></thead>
        <tbody>{rows.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((row, index) => <tr key={`${row.analysisRunId}-${row.issuerCik}-${index}`}>
          <td>{row.analysisDate}</td><td>{row.issuerSymbol}</td><td>{row.totalPurchaseValue}</td><td>{row.distinctInsiderCount}</td>
          <td>{row.averageValue === undefined ? "—" : money(row.averageValue)}</td><td>{row.outcome === "MISSING" ? "Missing evidence" : row.outcome === "PASS" ? "Passed" : "Did not pass"}</td>
        </tr>)}</tbody></table></div>
      <Pagination page={currentPage} pageSize={pageSize} total={rows.length} onPageChange={setPage} onPageSizeChange={(value) => {setPageSize(value); setPage(1);}}/>
      <p>Displayed averages are rounded; comparisons use the original precise purchase amount. This is not a minimum investment requirement for every buyer.</p>
    </details>
  </section>;
}
